// Minimal WebGL fragment-shader runner.
// ESM port of the handoff's shader-runner.js — no window globals.
// Usage: const r = new ShaderRunner(canvasEl, fragSrc, { autoDrift: true });
//        r.destroy();
const VERT = `
  attribute vec2 a_pos;
  void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
    throw new Error('shader compile failed');
  }
  return s;
}

export class ShaderRunner {
  constructor(canvas, fragSrc, opts = {}) {
    this.canvas = canvas;
    this.opts = opts;
    const gl = canvas.getContext('webgl', { antialias: true, premultipliedAlpha: false });
    if (!gl) throw new Error('no webgl');
    this.gl = gl;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, fragSrc);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(prog));
      throw new Error('program link failed');
    }
    this.prog = prog;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.u = {
      res: gl.getUniformLocation(prog, 'u_res'),
      time: gl.getUniformLocation(prog, 'u_time'),
      mouse: gl.getUniformLocation(prog, 'u_mouse'),
      smooth: gl.getUniformLocation(prog, 'u_smooth'),
      click: gl.getUniformLocation(prog, 'u_click'),
      clickT: gl.getUniformLocation(prog, 'u_clickT'),
      down: gl.getUniformLocation(prog, 'u_down'),
    };

    this.mouse = [0.5, 0.5];
    this.smooth = [0.5, 0.5];
    this.click = [0.5, 0.5];
    this.clickTime = -999;
    this.down = 0;
    this.start = performance.now();
    this.autoDrift = !!opts.autoDrift;
    this.hovered = false;

    const rect = () => canvas.getBoundingClientRect();
    this._onMove = (e) => {
      const r = rect();
      this.hovered = true;
      this.mouse = [(e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height];
    };
    this._onEnter = () => { this.hovered = true; };
    this._onLeave = () => {
      this.hovered = false;
      if (!this.autoDrift) this.mouse = [0.5, 0.5];
    };
    this._onUp = () => { this.down = 0; };
    canvas.addEventListener('mousemove', this._onMove);
    canvas.addEventListener('mouseenter', this._onEnter);
    canvas.addEventListener('mouseleave', this._onLeave);
    window.addEventListener('mouseup', this._onUp);

    this.resize();
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(canvas);
    this.ro = ro;

    this._frame = this._frame.bind(this);
    this._raf = requestAnimationFrame(this._frame);
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.floor(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.gl.viewport(0, 0, w, h);
  }

  _frame() {
    const gl = this.gl;
    const t = (performance.now() - this.start) / 1000;

    if (this.autoDrift && !this.hovered) {
      const u = 0.5 + 0.38 * Math.sin(t * 0.11);
      const v = 0.5 + 0.40 * Math.sin(t * 0.17 + 1.3);
      this.mouse = [u, v];
    }

    const k = 0.08;
    this.smooth[0] += (this.mouse[0] - this.smooth[0]) * k;
    this.smooth[1] += (this.mouse[1] - this.smooth[1]) * k;

    gl.uniform2f(this.u.res, this.canvas.width, this.canvas.height);
    gl.uniform1f(this.u.time, t);
    gl.uniform2f(this.u.mouse, this.mouse[0], this.mouse[1]);
    gl.uniform2f(this.u.smooth, this.smooth[0], this.smooth[1]);
    gl.uniform2f(this.u.click, this.click[0], this.click[1]);
    gl.uniform1f(this.u.clickT, t - this.clickTime);
    gl.uniform1f(this.u.down, this.down);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    this._raf = requestAnimationFrame(this._frame);
  }

  destroy() {
    cancelAnimationFrame(this._raf);
    this.ro.disconnect();
    this.canvas.removeEventListener('mousemove', this._onMove);
    this.canvas.removeEventListener('mouseenter', this._onEnter);
    this.canvas.removeEventListener('mouseleave', this._onLeave);
    window.removeEventListener('mouseup', this._onUp);
  }
}
